import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MAX_MEDIA_NAME_LENGTH } from 'constants/MediaConstants';
import Button from 'ui/base/Button';
import Checkbox from 'ui/base/Checkbox';
import Input from 'ui/base/Input';

import Thumbnail from './Thumbnail';
import { EditFilePanel, EditInput, EditMediaContent, EditSaveInLibrary, EditThumbnail, EditTitle } from './UI';
import { StarButton } from '../MediaLibrary/UI';

/** @typedef {{
 * onClose: () => void,
 * onSave: (media: import('modules/mediaLibrary/types').UploadModalMedia) => void,
 * media: import('modules/mediaLibrary/types').UploadModalMedia,
 * showAddToLibrary: boolean
 * }} EditDrawerProps */
/** @type {React.FC<EditDrawerProps>} */
const EditDrawer = ({ onClose, onSave, media, showAddToLibrary }) => {
  const intl = useIntl();
  const [updatedMedia, setUpdatedMedia] = React.useState(media);
  const handleNameChange = event => {
    setUpdatedMedia({ ...updatedMedia, name: event.target.value });
  };
  const handleSave = () => {
    onSave({ ...updatedMedia, name: updatedMedia.name?.trim() });
  };
  const toggleFavorite = () => setUpdatedMedia({ ...updatedMedia, isFavorite: !updatedMedia.isFavorite });
  const toggleAddToMediaLibrary = () =>
    setUpdatedMedia({ ...updatedMedia, addToMediaLibrary: !updatedMedia.addToMediaLibrary, isFavorite: false });

  const { isFavorite } = updatedMedia;
  const iconTheme = isFavorite ? 'fb-icon--star-filled' : 'fb-icon--star';

  return (
    <EditFilePanel>
      <EditMediaContent>
        <div />
        <EditTitle>
          <FormattedMessage id="name" defaultMessage="Name" />
        </EditTitle>
        <EditTitle>
          {!showAddToLibrary && <FormattedMessage id="mediaLibrary.favorite" defaultMessage="Favorite" />}
          {showAddToLibrary && <FormattedMessage id="mediaLibrary.saveInLibrary" defaultMessage="Save in my library" />}
        </EditTitle>
        <EditThumbnail>
          <Thumbnail media={media} />
        </EditThumbnail>
        <EditInput>
          <Input
            value={updatedMedia.name}
            data-cy="input-fileName"
            onChange={handleNameChange}
            maxLength={MAX_MEDIA_NAME_LENGTH}
            placeholder={intl.formatMessage({ id: 'mediaLibrary.fileName', defaultMessage: 'File name' })}
          />
        </EditInput>
        <EditSaveInLibrary>
          {showAddToLibrary && (
            <Checkbox
              className="ml-3 mr-4"
              checked={updatedMedia.addToMediaLibrary}
              onChange={toggleAddToMediaLibrary}
            />
          )}
          <StarButton
            onClick={toggleFavorite}
            disabled={!updatedMedia.addToMediaLibrary}
            data-cy={`star-button-filled-${isFavorite}`}
            icon={<span className={iconTheme} />}
            className="fb-icon"
          />
        </EditSaveInLibrary>
      </EditMediaContent>
      <div className="d-flex justify-content-center align-items-center w-100 mb-4 mt-4">
        <Button className="mr-4" onClick={onClose}>
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>
        <Button type="primary" onClick={handleSave}>
          <FormattedMessage id="save" defaultMessage="Save" />
        </Button>
      </div>
    </EditFilePanel>
  );
};

export default EditDrawer;
